import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'

// Custos com Plano de Saúde (RPLS002 / RPLS005 do ERP): parte do funcionário e da empresa no mês

interface Linha {
  motcod: string; nome: string; plano: string; tempo?: string; salario?: number; idade?: number
  dependentes: number; funcionario: number; empresa: number; total: number; alterou: boolean
}
interface Relatorio { competencia: string; tipo: 'S' | 'I'; linhas: Linha[]; totFuncionario: number; totEmpresa: number; totGeral: number }

const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro']
const moeda = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const th: React.CSSProperties = { padding: '0.45rem 0.6rem', fontSize: '0.72rem' }
const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }
const num: React.CSSProperties = { textAlign: 'right', whiteSpace: 'nowrap' }

export default function RelatorioCustoPlanoSaude() {
  const { empresaCod } = useEmpresa()
  const hoje = new Date()
  const [ano, setAno] = useState(hoje.getFullYear())
  const [mes, setMes] = useState(hoje.getMonth() + 1)
  const [tipo, setTipo] = useState<'S' | 'I'>('I')
  const [rel, setRel] = useState<Relatorio | null>(null)
  const [erro, setErro] = useState('')
  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)

  const params = () => ({ ano, mes, tipo, empresa: empresaCod ?? 1 })

  async function consultar() {
    setErro(''); setCarregando(true)
    try {
      const r = await api.get('/planos-saude/relatorio/custos', { params: params() })
      setRel(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar o relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function pdf() {
    setErro(''); setGerandoPdf(true)
    try {
      const r = await api.get('/planos-saude/relatorio/custos', { params: { ...params(), formato: 'pdf' }, responseType: 'blob' })
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank')
    } catch (e: any) {
      let msg = 'Erro ao gerar o PDF.'
      try { msg = JSON.parse(await e?.response?.data?.text()).error || msg } catch {}
      setErro(msg)
    } finally {
      setGerandoPdf(false)
    }
  }

  return (
    <div style={{ maxWidth: 1100 }}>
      <h2 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
        Custos com Plano de Saúde
        <span style={{ color: '#9ca3af', fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 400 }}>RPLS002 / RPLS005</span>
      </h2>

      <div className="card">
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ margin: 0 }}>
            <label>Mês</label>
            <select value={mes} onChange={e => setMes(Number(e.target.value))}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Ano</label>
            <input type="number" value={ano} style={{ width: '9ch' }} onChange={e => setAno(Number(e.target.value))} />
          </div>
          <div className="field" style={{ margin: 0 }}>
            <label>Cálculo</label>
            <select value={tipo} onChange={e => { setTipo(e.target.value as 'S' | 'I'); setRel(null) }}>
              <option value="I">Por faixa etária</option>
              <option value="S">Por salário / tempo de serviço</option>
            </select>
          </div>
          <button className="btn btn-primary" onClick={consultar} disabled={carregando}>{carregando ? 'Consultando...' : 'Consultar'}</button>
          <button className="btn btn-secondary" onClick={pdf} disabled={gerandoPdf}>{gerandoPdf ? 'Gerando...' : 'PDF'}</button>
        </div>
        <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.75rem 0 0' }}>
          Entram os funcionários com carteira de plano, admitidos até o 1º dia do mês e não demitidos. O valor só aparece se o plano
          tiver uma validade que cubra o mês (Cadastro de Plano de Saúde).
        </p>
        {erro && <div style={{ color: '#b91c1c', fontSize: '0.85rem', marginTop: '0.75rem' }}>{erro}</div>}
      </div>

      {rel && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
            {rel.competencia.charAt(0).toUpperCase() + rel.competencia.slice(1)} · {rel.linhas.length} funcionário(s)
          </h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: '0.9rem' }}>
            {([['Funcionário', rel.totFuncionario, '#b45309'], ['Empresa', rel.totEmpresa, '#1b3f7b'], ['Total', rel.totGeral, '#15803d']] as [string, number, string][]).map(([t, v, c]) => (
              <div key={t} style={{ flex: '1 1 160px', border: '1px solid #e5e7eb', borderLeft: `4px solid ${c}`, borderRadius: 8, padding: '0.55rem 0.8rem' }}>
                <div style={{ fontSize: '0.7rem', color: '#6b7280', textTransform: 'uppercase', fontWeight: 600 }}>{t}</div>
                <div style={{ fontSize: '1.15rem', fontWeight: 700, color: c }}>R$ {moeda(v)}</div>
              </div>
            ))}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={th}>Código</th><th style={th}>Nome</th><th style={th}>Plano</th>
                  {rel.tipo === 'S' ? <><th style={th}>Tempo serv.</th><th style={{ ...th, ...num }}>Salário</th></> : <th style={{ ...th, ...num }}>Idade</th>}
                  <th style={{ ...th, ...num }}>Dependentes</th>
                  <th style={{ ...th, ...num }}>Funcionário</th><th style={{ ...th, ...num }}>Empresa</th><th style={{ ...th, ...num }}>Total</th>
                  <th style={th} title="Valor diferente do mês anterior"></th>
                </tr>
              </thead>
              <tbody>
                {rel.linhas.length === 0 && (
                  <tr><td colSpan={10} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum funcionário com plano para o mês.</td></tr>
                )}
                {rel.linhas.map(l => (
                  <tr key={l.motcod}>
                    <td style={td}>{l.motcod}</td><td style={td}>{l.nome}</td><td style={td}>{l.plano}</td>
                    {rel.tipo === 'S'
                      ? <><td style={td}>{l.tempo}</td><td style={{ ...td, ...num }}>{moeda(l.salario ?? 0)}</td></>
                      : <td style={{ ...td, ...num }}>{l.idade ?? '—'}</td>}
                    <td style={{ ...td, ...num }}>{l.dependentes}</td>
                    <td style={{ ...td, ...num }}>{moeda(l.funcionario)}</td>
                    <td style={{ ...td, ...num }}>{moeda(l.empresa)}</td>
                    <td style={{ ...td, ...num, fontWeight: 700 }}>{moeda(l.total)}</td>
                    <td style={{ ...td, color: '#b45309', fontWeight: 700 }} title={l.alterou ? 'Valor diferente do mês anterior' : undefined}>{l.alterou ? '*' : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.72rem', color: '#6b7280', margin: '0.6rem 0 0' }}>* valor diferente do mês anterior.</p>
        </div>
      )}
    </div>
  )
}
